/**
 * Registre des imports faits sans compte, en attente d'être revendiqués.
 *
 * Ce n'est **pas** une « liste locale ». C'est une dizaine de lignes qui ne
 * s'affichent jamais telles quelles : leur seul rôle est de pouvoir dire au
 * serveur, à l'inscription ou à la connexion, « ces fichiers-là sont les
 * miens ». Le jour où la liste existe côté serveur, ce registre ne sert plus
 * qu'aux imports faits entre-temps.
 *
 * La distinction compte : construire une vraie liste locale puis la migrer
 * aurait été du travail à jeter, et surtout deux sources de vérité pendant
 * plusieurs semaines.
 *
 * ## Ce que le registre garantit
 *
 *   - Il ne se vide que sur confirmation du serveur. Un appel qui échoue laisse
 *     tout en place, et la revendication se rejoue au montage suivant — c'est
 *     ce qui rend `POST /api/library/claim` utile d'être idempotent.
 *   - Il ne fait jamais échouer un import. Une écriture impossible (quota
 *     saturé, mode privé) est avalée : perdre la revendication d'un fichier est
 *     regrettable, interrompre l'import qui vient de réussir ne l'est pas.
 *   - Il est plafonné. Au-delà, ce n'est plus un rattrapage d'imports.
 */

export type PendingClaim = {
  /** L'identifiant que le serveur a rendu à l'upload. */
  readonly midiFileId: string;
  /** Le titre, uniquement pour pouvoir écrire « tu as importé 2 morceaux ». */
  readonly title: string;
  readonly addedAt: string;
};

const STORAGE_KEY = "virtuosa:pending-claims";

/**
 * Même plafond que côté serveur (`ClaimLibraryRequest`). Les deux doivent
 * bouger ensemble : un registre plus large produirait des requêtes rejetées.
 */
const MAX_ENTRIES = 50;

function readRaw(): PendingClaim[] {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (raw === null) {
      return [];
    }

    const parsed: unknown = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(isPendingClaim);
  } catch {
    // JSON illisible : on repart d'un registre vide plutôt que de propager.
    // On ne supprime pas la clé pour autant, la leçon de `progress-store` vaut
    // ici aussi — une lecture ne détruit pas.
    return [];
  }
}

function isPendingClaim(value: unknown): value is PendingClaim {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Partial<PendingClaim>;

  return (
    typeof candidate.midiFileId === "string" &&
    typeof candidate.title === "string" &&
    typeof candidate.addedAt === "string"
  );
}

function write(entries: readonly PendingClaim[]): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Quota saturé ou stockage refusé. On ne remonte rien : l'import vient de
    // réussir, il n'a pas à échouer parce qu'on n'a pas pu noter son id.
  }
}

/** Le registre, du plus récent au plus ancien. */
export function readPendingClaims(): readonly PendingClaim[] {
  return readRaw();
}

export function countPendingClaims(): number {
  return readRaw().length;
}

/**
 * Note un import anonyme. Sans effet si la personne est déjà connectée —
 * l'entrée existe alors déjà côté serveur, l'inscrire ici ne ferait que créer
 * une revendication à rejouer pour rien.
 */
export function recordPendingClaim(
  midiFileId: string,
  title: string,
  isAuthenticated: boolean,
): void {
  if (isAuthenticated) {
    return;
  }

  const existing = readRaw().filter((entry) => entry.midiFileId !== midiFileId);

  write(
    [
      { midiFileId, title, addedAt: new Date().toISOString() },
      ...existing,
    ].slice(0, MAX_ENTRIES),
  );
}

/**
 * Retire du registre ce que le serveur a confirmé avoir traité.
 *
 * Appelé avec les identifiants envoyés, pas avec un compteur : une
 * revendication partielle — certains fichiers introuvables — ne doit pas
 * laisser traîner indéfiniment ceux qui ne se rattacheront jamais.
 */
export function clearPendingClaims(midiFileIds: readonly string[]): void {
  const claimed = new Set(midiFileIds);
  const remaining = readRaw().filter((entry) => !claimed.has(entry.midiFileId));

  if (remaining.length === 0) {
    if (typeof window !== "undefined") {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Rien à faire de plus : le registre sera simplement rejoué.
      }
    }

    return;
  }

  write(remaining);
}
